import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import {
  Button,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { usePromptsStore } from "./promptsStore";
import { useBrand } from "../Brands/useBrand";
import { promptProvenance } from "./promptSource";
import { QUERY_TYPE_ORDER, queryTypeMeta } from "../_Shared/queryTypes";
import QueryTypeBadge from "../_Shared/QueryTypeBadge";
import {
  TARGET_COUNTRIES,
  TARGET_LANGUAGES,
  formatMarket,
} from "../_Shared/targetMarkets";

export default function CreatePrompt({
  modalOpened,
  setModalOpened,
  brandId,
  initialValues,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createPrompt, updatePrompt } = usePromptsStore();
  const brand = useBrand(brandId);

  const isEdit = !!initialValues?.id;

  const { t } = useTranslation("prompts", {
    keyPrefix: "createModal",
  });

  const projectInitialValues = (data) => {
    let queryType = data?.queryType || "";
    // Normalize aspect-specific types back to base types for the form
    if (queryType.startsWith("aspectSpecific")) {
      queryType = queryType.replace("aspectSpecific", "");
      queryType = queryType.charAt(0).toLowerCase() + queryType.slice(1);
    }
    const followBrand = !data?.defaultTargetCountryCode && !data?.defaultTargetLanguageCode;
    return {
      brandId: brand.id,
      displayName: data?.displayName || "",
      prompt: data?.prompt || "",
      aspect: data?.aspect || "",
      industry: data?.industry || "",
      queryType,
      status: data?.status || "auto",
      followBrand,
      defaultTargetCountryCode:
        data?.defaultTargetCountryCode || brand.defaultTargetCountryCode || "US",
      defaultTargetLanguageCode:
        data?.defaultTargetLanguageCode || brand.defaultTargetLanguageCode || "en",
    };
  };

  const form = useForm({
    initialValues: projectInitialValues(initialValues),
    validate: {
      prompt: (value) =>
        value.trim().length > 0 ? null : t("validation.promptRequired"),
      queryType: (value) =>
        value ? null : "Please select a query type",
    },
  });

  useEffect(() => {
    const updatedValues = projectInitialValues(initialValues);
    console.log("Initial values for form:", initialValues);
    form.setInitialValues(updatedValues);
    form.setValues(updatedValues);
  }, [initialValues]);

  const handleSubmit = (values) => {
    setIsSubmitting(true);
    const payload = {
      ...values,
      defaultTargetCountryCode: values.followBrand
        ? null
        : values.defaultTargetCountryCode,
      defaultTargetLanguageCode: values.followBrand
        ? null
        : values.defaultTargetLanguageCode,
    };
    delete payload.followBrand;
    if (isEdit) {
      updatePrompt(initialValues.id, payload)
        .then((updatedPrompt) => {
          notifications.show({
            title: t("notifications.updateSuccessTitle", "Prompt Modified"),
            // message: t("notifications.updateSuccessMessage", {
            //   name: updatedPrompt.displayName,
            // }),
            color: "green",
          });
          setModalOpened(false);
          form.reset();
          // setLocation(`/brands/${brandId}/prompts`);
        })
        .catch((error) => {
          console.error("Failed to update prompt:", error);
          notifications.show({
            title: t("notifications.updateErrorTitle", "Update Failed"),
            message:
              error?.message ||
              t("notifications.updateErrorMessage", "Failed to update prompt"),
            color: "red",
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      createPrompt(payload)
        .then((prompt) => {
          // not needed, we're redirecting to the edit page anyway
          // setReportTemplates((prevTemplates) => [...prevTemplates, newTemplate]);
          notifications.show({
            title: t("notifications.createSuccessTitle", "Prompt Created"),
            // message: t("notifications.createSuccessMessage", {
            //   title: prompt.title,
            // }),
            color: "green",
          });
          setModalOpened(false);
          form.reset();
          // setLocation(`/brands/edit/${brand.id}`);
        })
        .catch((error) => {
          console.error("Failed to create prompt:", error);
          notifications.show({
            title: t("notifications.createErrorTitle", "Create Failed"),
            message:
              error?.message ||
              t("notifications.createErrorMessage", "Failed to create prompt"),
            color: "red",
          });
          form.reset();
          // setLocation(`/brands/edit/${brand.id}`);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  /**
   * The closed set an industry may take, mirroring BrandIndustryVocabulary on the server:
   * categories first (the useful granularity), then industries (the coarse fallback).
   *
   * A Select, not the Autocomplete this used to be. Industry is the key every report row is grouped
   * on, so a free-text box let one careless entry create a report row holding a single prompt — the
   * same way the generated prompts produced twelve near-identical industries. The server snaps
   * anything outside this list anyway; offering only valid choices means the value you pick is the
   * value you get.
   */
  const industryData = [
    ...(brand?.categories ?? []).map((c) => c.englishName || c.name),
    ...(brand?.industries ?? []),
  ]
    .filter(Boolean)
    .filter((value, i, all) => all.findIndex((v) => v.toLowerCase() === value.toLowerCase()) === i)
    .map((value) => ({ value, label: value }));

  const aspectData = brand?.topics?.map((topic) => ({
    value: topic.name,
    label: topic.name,
  }));

  const provenance = isEdit ? promptProvenance(initialValues) : null;

  return (
    <Modal
      opened={modalOpened}
      onClose={() => {
        setModalOpened(false);
        form.reset();
      }}
      title={t(
        isEdit ? "editTitle" : "title",
        isEdit ? "Edit Prompt" : "Create Prompt"
      )}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label={t("labels.displayName", "Name")}
            placeholder={t("placeholders.displayName", "Enter prompt name")}
            {...form.getInputProps("displayName")}
          />
          {/* The aspect-specific variants are not offered: the server derives them from whether an
              aspect is filled in, so listing them here would let the two disagree. */}
          <Select
            label={t("labels.queryType", "Query Type")}
            placeholder={t("placeholders.queryType", "Select query type")}
            description={
              form.values.queryType
                ? queryTypeMeta(form.values.queryType).tooltip
                : "What kind of measurement this prompt is"
            }
            data={QUERY_TYPE_ORDER.filter(
              (value) => !value.startsWith("aspectSpecific")
            ).map((value) => ({ value, label: queryTypeMeta(value).label }))}
            renderOption={({ option }) => (
              <Group gap="xs" wrap="nowrap">
                <QueryTypeBadge queryType={option.value} />
                <Text size="sm">{queryTypeMeta(option.value).label}</Text>
              </Group>
            )}
            {...form.getInputProps("queryType")}
            required
          />
          <Select
            label={t("labels.category", "Category / Industry")}
            placeholder={t("placeholders.category", "Select category")}
            description="Groups this prompt with others in your reports. Add a category in brand settings if the one you need is missing."
            data={industryData}
            searchable
            {...form.getInputProps("industry")}
          />
          <Select
            label={t("labels.aspect", "Aspect")}
            placeholder={t("placeholders.aspect", "Select aspect")}
            data={aspectData}
            {...form.getInputProps("aspect")}
          />
          <Textarea
            label={t("labels.prompt", "Prompt")}
            placeholder={t("placeholders.prompt", "Enter prompt")}
            {...form.getInputProps("prompt")}
            required
            minRows={2}
            maxRows={8}
            autosize
          />
          <Switch
            label="Use the brand's home market"
            description={
              brand
                ? `Home is ${formatMarket(brand.defaultTargetCountryCode, brand.defaultTargetLanguageCode) || "not set"}. You can write in English; the question is still asked as this market.`
                : "Follow the brand"
            }
            {...form.getInputProps("followBrand", { type: "checkbox" })}
          />
          {!form.values.followBrand && (
            <Stack gap={6}>
              <Group grow>
                <Select
                  label="Country"
                  description="The market, not the language you type in"
                  data={TARGET_COUNTRIES}
                  searchable
                  allowDeselect={false}
                  {...form.getInputProps("defaultTargetCountryCode")}
                />
                <Select
                  label="Language"
                  description="How people there search and read"
                  data={TARGET_LANGUAGES}
                  searchable
                  allowDeselect={false}
                  {...form.getInputProps("defaultTargetLanguageCode")}
                />
              </Group>
              <Text size="xs" c="dimmed">
                A second country is a second measurement and uses the same
                watch-list as another prompt.
              </Text>
            </Stack>
          )}

          {/* Who wrote this, how, and when — the first thing worth knowing before editing a prompt
              that measures the wrong thing, and previously unanswerable from anywhere in the UI. */}
          {provenance && (
            <>
              <Divider />
              <Text size="xs" c="dimmed">
                {provenance.sentence}
              </Text>
            </>
          )}

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setModalOpened(false);
                form.reset();
              }}
              disabled={isSubmitting}
            >
              {t("buttons.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isEdit
                ? t("buttons.save", "Save")
                : t("buttons.create", "Create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
